import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

import { TokenRestrictionCode, TokenRestrictedActions, RestrictionExpanded } from "../types";
import { useAbfTypesToUiConverter } from "../utils/ui";
import { useTokenRestrictionsQuery } from "../reducers/tokenListApi";
import { useUserCountryQuery } from "../reducers/geoApi";

interface TokenRestrictionsContextValue {
    getTokenRestrictionsForMints: (params: {
        mints: string[] | undefined;
        restrictionCodes?: TokenRestrictionCode[];
        relevantActions?: TokenRestrictedActions[];
        isActive?: boolean;
    }) => RestrictionExpanded[] | undefined;
    isTokenRestrictionsLoading: boolean;
}

const TokenRestrictionsContext = createContext<TokenRestrictionsContextValue | undefined>(undefined);

export function TokenRestrictionsProvider({ children }: { children: ReactNode }) {
    const { data: tokenRestrictions, isLoading: isTokenRestrictionsLoading } = useTokenRestrictionsInfo();

    const getTokenRestrictionsForMints = useCallback(
        ({
            mints,
            restrictionCodes,
            relevantActions,
            isActive
        }: {
            mints: string[] | undefined;
            restrictionCodes?: TokenRestrictionCode[];
            relevantActions?: TokenRestrictedActions[];
            isActive?: boolean;
        }) => {
            if (mints === undefined || mints.length === 0 || !tokenRestrictions) return undefined;

            // Filter already-converted restrictions
            const filtered = tokenRestrictions.filter((restriction) => {
                // Filter by mint
                if (!mints.includes(restriction.token?.mint ?? "")) return false;

                // Filter by isActive if provided
                if (isActive !== undefined && isActive !== restriction.isActive) {
                    return false;
                }

                // Filter by restrictionCode if provided
                if (restrictionCodes && !restrictionCodes.includes(restriction.restrictionCode)) {
                    return false;
                }

                // Filter by restrictedActions if provided (must have at least one matching action)
                if (relevantActions && relevantActions.length > 0) {
                    const hasMatchingAction =
                        restriction.restrictedActions.includes(TokenRestrictedActions.All) ||
                        restriction.restrictedActions.some((action) => relevantActions.includes(action));
                    if (!hasMatchingAction) {
                        return false;
                    }
                }

                return true;
            });

            // Scope the restrictedActions array to only include relevant actions
            const scoped =
                relevantActions && relevantActions.length > 0
                    ? filtered.map((r) => ({
                          ...r,
                          restrictedActions: r.restrictedActions.filter(
                              (action) => relevantActions.includes(action) || action === TokenRestrictedActions.All
                          )
                      }))
                    : filtered;

            return scoped.length > 0 ? scoped : undefined;
        },
        [tokenRestrictions]
    );

    const value = useMemo<TokenRestrictionsContextValue>(
        () => ({
            getTokenRestrictionsForMints,
            isTokenRestrictionsLoading
        }),
        [getTokenRestrictionsForMints, isTokenRestrictionsLoading]
    );

    return <TokenRestrictionsContext.Provider value={value}>{children}</TokenRestrictionsContext.Provider>;
}

export function useTokenRestrictionsContext() {
    const context = useContext(TokenRestrictionsContext);
    if (!context) {
        throw new Error("useTokenRestrictionsContext must be used within a TokenRestrictionsProvider");
    }
    return context;
}

export function useTokenRestrictionsInfo() {
    const { data: userCountry, isLoading: isCountryLoading } = useUserCountryQuery(undefined, {
        refetchOnMountOrArgChange: false
    });
    const { data: rawRestrictions, isLoading: isRestrictionsLoading } = useTokenRestrictionsQuery(
        {
            countryCode: userCountry?.country_code
        },
        {
            skip: !userCountry?.country_code
        }
    );
    const { convertTokenRestrictions } = useAbfTypesToUiConverter();

    const converted = useMemo(
        () => (rawRestrictions ? convertTokenRestrictions(rawRestrictions) : undefined),
        [rawRestrictions, convertTokenRestrictions]
    );

    return { data: converted, isLoading: isCountryLoading || isRestrictionsLoading };
}
